import React, { useRef, useState, useEffect } from 'react';
import './carousel.scss';
import sound from '../../assets/carousel/sound.svg';
import mute from '../../assets/carousel/mute.svg';
import nsAudio from '../../assets/carousel/novaScotia/ns_audio.mp3';
import onAudio from '../../assets/carousel/ontario/on_audio.mp3';
import bcAudio from '../../assets/carousel/bc/bc_audio.mp3';
import { carouselData } from './carouselData';
import Carousel2 from './Carousel2';
import { ReactComponent as Arrow } from '../../assets/carousel/arrow_carousel.svg';

const audios = [new Audio(nsAudio), new Audio(onAudio), new Audio(bcAudio)];

function Carousel({ dataIndex, carouselRef, videoSrc }) {
  const container = useRef();
  const currentAudioRef = useRef();
  const [current, setCurrent] = useState(0);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [playing, setPlaying] = useState(false);

  const data = carouselData[dataIndex];
  const length = data.contents.length - 1;

  const handleClickAudio = (audio) => {
    if (playing) {
      audios[currentAudio].pause();
      if (currentAudio !== audio) {
        setPlaying(true);
        audios[audio].play();
      } else {
        setPlaying(false);
      }
    } else {
      setPlaying(true);
      audios[audio].play();
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach((entry) => {
          if (entry.intersectionRatio > 0.3) {
            carouselRef.current.style.opacity = '1';
          }
        });
      },
      { threshold: [0, 0.3] }
    );
    observer.observe(carouselRef.current);
  }, [carouselRef]);

  useEffect(() => {
    if (currentAudio !== null) {
      currentAudioRef.current = audios[currentAudio];
      currentAudioRef.current.addEventListener('ended', () => {
        setPlaying(false);
        currentAudio(null);
      });
    }
  }, [currentAudio]);

  return (
    <section className={`carousel carousel--${dataIndex}`} ref={carouselRef}>
      <h2 className="carouselHead">{data.province}</h2>
      <h3 className="carouselSubhead">See For Yourself</h3>
      <div className="carousel__indicator">
        {data.contents.map((content, i) => {
          return (
            <svg
              key={i}
              className={
                current === i
                  ? 'carousel__indicator__circle carousel__indicator__circle-active'
                  : 'carousel__indicator__circle'
              }
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 16 16"
            >
              <circle cx="8" cy="8" r="6" />
            </svg>
          );
        })}
      </div>
      <div className="carousel__contents">
        <div className="carousel__contents__arrows">
          <button
            className={
              current === 0
                ? `carousel__contents__arrows-left carousel__contents__arrows-left-${dataIndex} carousel__contents__arrows-inactive`
                : `carousel__contents__arrows-left carousel__contents__arrows-left-${dataIndex}`
            }
            aria-label="Go to previous slide"
          >
            <Arrow />
          </button>
          <button
            className={
              current === length
                ? `carousel__contents__arrows-right-${dataIndex} carousel__contents__arrows-inactive`
                : `carousel__contents__arrows-right-${dataIndex}`
            }
            aria-label="Go to next slide"
          >
            <Arrow />
          </button>
        </div>

        <Carousel2
          dataIndex={dataIndex}
          current={current}
          setCurrent={setCurrent}
          videoSrc={videoSrc}
          container={container}
        />
        <div className="carousel__contents__text" ref={container}>
          {data.contents[current].tip ? (
            <>
              <h4
                className={
                  data.province === 'Nova Scotia'
                    ? 'carouselTitle'
                    : 'carouselTitle carouselTitle--ontario'
                }
              >
                Local tip
              </h4>
              <blockquote className={data.province === 'Nova Scotia' ? 'copy' : 'copy localTip'}>
                {data.contents[current].copy}
              </blockquote>
              <p className="carouselName">
                <span className="carouselName--bold">{data.contents[current].name}</span>
                <br />
                {data.contents[current].from}
              </p>
            </>
          ) : (
            <>
              <h4 className="carouselTitle">{data.contents[current].title}</h4>
              {data.contents[current].title === 'Seafood galore' ? (
                <p className="copy">
                  From succulent lobster to the world’s largest scallop fishing fleet to
                  fresh-caught-this-morning fish, Nova Scotia is synonymous with seafood. “I
                  expected Nova Scotia to be breathtakingly beautiful and I also expected there to
                  be some incredible seafood,” Kia says. “And everything{' '}
                  <span className="it">was</span> incredible.”
                </p>
              ) : (
                <p className="copy">{data.contents[current].copy}</p>
              )}
              {data.contents[current].source && (
                <span className="carouselSource">
                  ©{' '}
                  <span className="carouselSource carouselSource--italic">
                    {data.contents[current].source}
                  </span>
                </span>
              )}

              {data.contents[current].audio ? (
                <button
                  className={
                    data.contents[current].source || data.contents[current].imgSrc
                      ? `carouselButton ${data.contents[current].audio} carousel__button--margin`
                      : `carouselButton ${data.contents[current].audio}`
                  }
                  onClick={() => {
                    handleClickAudio(data.contents[current].audioIndex);
                    setCurrentAudio(data.contents[current].audioIndex);
                  }}
                >
                  {playing && currentAudio === data.contents[current].audioIndex
                    ? 'stop'
                    : data.contents[current].button}{' '}
                  {playing && currentAudio === data.contents[current].audioIndex ? (
                    <img src={mute} alt="" className="muteIcon" />
                  ) : (
                    <img src={sound} alt="" className="soundIcon" />
                  )}
                </button>
              ) : (
                <></>
              )}
              {data.contents[current].href ? (
                <a
                  href={data.contents[current].href}
                  target="_blank"
                  rel="sponsored"
                  aria-label={data.contents[current].href}
                >
                  <div
                    className={
                      data.contents[current].source || data.contents[current].imgSrc
                        ? 'carouselButton carousel__button carousel__button--margin'
                        : 'carouselButton carousel__button'
                    }
                  >
                    {data.contents[current].button}
                  </div>
                </a>
              ) : (
                <></>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export default Carousel;
